import React, { useEffect, useState, useContext } from "react";
import { Container, Row, Col, Card, Form, Button } from "react-bootstrap";
import "./checkout.scss";
import { getUser } from "../utils/auth";
import api from "../admin/api/api";
import { toast } from "react-toastify";
import { CartContext } from "../context/CartContext";
import { useNavigate } from "react-router-dom";


const Checkout = () => {
  const [cartItems, setCartItems] = useState([]);
  const [billing, setBilling] = useState({
    firstName: "",
    lastName: "",
    address: "",
    city: "",
    postalCode: "",
    email: "",
    phone: ""
  });

  const navigate = useNavigate();
  const [shippingSetting, setShippingSetting] = useState(null);
  const [paymentSetting, setPaymentSetting] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("COD");

  const [errors, setErrors] = useState({});

  const { clearCart } = useContext(CartContext);

  useEffect(() => {
  loadData();
}, []);

const loadData = async () => {
  const cart = JSON.parse(localStorage.getItem("cart")) || [];
  setCartItems(cart);

  const user = getUser();

  try {
    const billingRes = await api.get(`/BillingAPI/GetByUser/${user.userId}`);
    if (billingRes.data) setBilling(billingRes.data);

    const shippingRes = await api.get("/Settings/shipping");
    setShippingSetting(shippingRes.data);

    // 🔥 Load Payment Settings
    const paymentRes = await api.get("/Settings/payment");
    setPaymentSetting(paymentRes.data);

  } catch (error) {
    console.log("Checkout API Error", error);
  }
};

  const handleChange = (e) => {
    const { name, value } = e.target;

    setBilling({
      ...billing,
      [name]: value
    });

    setErrors({
      ...errors,
      [name]: ""
    });
  };

  const validateForm = () => {
    let newErrors = {};

    if (!billing.firstName.trim())
      newErrors.firstName = "First name is required";

    if (!billing.lastName.trim())
      newErrors.lastName = "Last name is required";

    if (!billing.address.trim())
      newErrors.address = "Address is required";

    if (!billing.city.trim())
      newErrors.city = "City is required";

    if (!billing.postalCode.trim())
      newErrors.postalCode = "Postal code is required";

    if (!billing.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(billing.email)) {
      newErrors.email = "Invalid email format";
    }

    if (!billing.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^[0-9]{10}$/.test(billing.phone)) {
      newErrors.phone = "Phone must be 10 digits";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const subtotal = cartItems.reduce(
    (sum, item) => sum + Number(item.price) * Number(item.quantity),
    0
  );

  const shipping =
  shippingSetting && subtotal > shippingSetting.freeShippingAmount
    ? 0
    : shippingSetting?.standardCharge || 0;

  const codFee = shippingSetting?.codCharge || 0;
  const total = subtotal + shipping + codFee;


  const handlePlaceOrder = async () => {

  if (!validateForm()) return;

  const user = getUser();

  // 🔴 CHECK PAYMENT METHOD
  if (paymentMethod === "ONLINE") {
    toast.info("Online payment integration coming soon");
    return;
  }
 
  try {

    // 🔥 STEP 1: Save Billing Address (Default Address)
    await api.post("/BillingAPI/Save", {
      ...billing,
      userId: user.userId
    });

    // 🔥 STEP 2: Create Order
    const orderData = {
      userId: user.userId,
      subTotal: subtotal,
      shipping: shipping,
      codFee: codFee,
      totalAmount: total,
      paymentMethod: paymentMethod,

      // Snapshot Billing into Order
      firstName: billing.firstName,
      lastName: billing.lastName,
      address: billing.address,
      city: billing.city,
      postalCode: billing.postalCode,
      email: billing.email,
      phone: billing.phone,

      items: cartItems.map(item => ({
        productId: item.id,      // ✅ FIXED HERE
        productName: item.name,  // ✅ FIXED HERE
        quantity: item.quantity,
        price: item.price,
        size: item.size,              // you don't have size in cart
        color: item.color              // you don't have color in cart
      }))
    };

    const response = await api.post("/Orders/Create", orderData);
    
     console.log("Order API Response:", response.data);

    const orderId = response?.data?.orderId;

    if (!orderId) {
      toast.error("Order ID missing from API response");
      return;
    }

    toast.success("Order Placed Successfully");
   
    navigate(`/order-success/${orderId}`);

    clearCart();

  } catch (error) {
    console.error("Full Error:", error.response?.data || error);
    toast.error("Order Failed");
  }
};
  

  return (
    <Container className="my-5 pt-5">
      <Row className="mb-2">
        <Col>
          <h3 className="fw-bold border-bottom pb-3">Checkout</h3>
        </Col>
      </Row>

      <Row>
        {/* LEFT SIDE - BILLING DETAILS */}
        <Col md={8} className="mt-3">
          <Card className="p-4 shadow-sm border-1 mb-4">
            <h5 className="fw-bold mb-3 pb-2 border-bottom border-1">
              Billing Details
            </h5>

            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>First Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="firstName"
                      value={billing.firstName}
                      onChange={handleChange}
                      placeholder="Enter first name"
                      isInvalid={!!errors.firstName}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.firstName}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Last Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="lastName"
                      value={billing.lastName}
                      onChange={handleChange}
                      placeholder="Enter last name"
                      isInvalid={!!errors.lastName}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.lastName}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Address</Form.Label>
                <Form.Control
                  type="text"
                  name="address"
                  value={billing.address}
                  onChange={handleChange}
                  placeholder="Street address"
                  isInvalid={!!errors.address}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.address}
                </Form.Control.Feedback>
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>City</Form.Label>
                    <Form.Control
                      type="text"
                      name="city"
                      value={billing.city}
                      onChange={handleChange}
                      placeholder="Enter city"
                      isInvalid={!!errors.city}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.city}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Postal Code</Form.Label>
                    <Form.Control
                      type="text"
                      name="postalCode"
                      value={billing.postalCode}
                      onChange={handleChange}
                      placeholder="Enter postal code"
                      isInvalid={!!errors.postalCode}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.postalCode}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={billing.email}
                  onChange={handleChange}
                  placeholder="Enter email"
                  isInvalid={!!errors.email}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.email}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Phone</Form.Label>
                <Form.Control
                  type="text"
                  name="phone"
                  value={billing.phone}
                  onChange={handleChange}
                  placeholder="Enter phone number"
                  isInvalid={!!errors.phone}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.phone}
                </Form.Control.Feedback>
              </Form.Group>
            </Form>
          </Card>

          {/* PAYMENT METHOD */}
          <Card className="p-4 shadow-sm border-1">
            <h5 className="fw-bold mb-3 pb-2 border-bottom border-1">
              Payment Method
            </h5>

            <Form>
  {paymentSetting?.enableCOD && (
    <Form.Check
      type="radio"
      label="Cash on Delivery"
      name="payment"
      value="COD"
      checked={paymentMethod === "COD"}
      onChange={(e) => setPaymentMethod(e.target.value)}
      className="mb-2"
    />
  )}

  {paymentSetting?.enableRazorpay && (
    <Form.Check
      type="radio"
      label="UPI / Credit / Debit Card"
      name="payment"
      value="ONLINE"
      checked={paymentMethod === "ONLINE"}
      onChange={(e) => setPaymentMethod(e.target.value)}
      className="mb-2"
    />
  )}
</Form>
          </Card>
        </Col>

        {/* RIGHT SIDE - ORDER SUMMARY */}
        <Col md={4} className="mt-3">
          <Card className="p-4 shadow-sm border-1">
            <h5 className="fw-bold mb-3 pb-2 border-bottom border-1">
              Order Summary
            </h5>

            <div className="d-flex justify-content-between mb-2">
              <span>Subtotal</span>
              <span>₹ {subtotal}</span>
            </div>

            <div className="d-flex justify-content-between mb-2">
  <span>Shipping</span>
  <span>{shipping === 0 ? "FREE" : `₹ ${shipping}`}</span>
</div>

{shipping === 0 ? (
  <div className="text-success small">
    🎉 You got free shipping
  </div>
) : (
  <div className="text-muted small">
    Free delivery on orders above ₹{shippingSetting?.freeShippingAmount}
  </div>
)}

<div className="text-muted small">
  Estimated delivery in {shippingSetting?.deliveryDays} days
</div>

            <div className="d-flex justify-content-between mb-2">
  <span>COD Fee</span>
  <span>₹ {codFee}</span>
</div>

<div className="text-muted small">
  Cash on Delivery handling charge
</div>

            <hr />

            <div className="d-flex justify-content-between fw-bold mb-3">
              <span>Order Total</span>
              <span>₹ {total}</span>
            </div>

            <Button variant="dark" className="w-100" onClick={handlePlaceOrder}>
              Place Order
            </Button>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Checkout;